import axios from 'axios';
import { HmacSHA256 } from 'crypto-js';
import { NAMESPACES } from './configuration';

export function CreatePasswordHash(password: string): string {
  const generatorKey = 'mediopunto';
  const hash = HmacSHA256(password, generatorKey).toString();
  return hash;
}

export const GetToken = (): string | null => {
    const  token = localStorage.getItem('access_token');
    if (token === '') {
      console.error('No token found. Authentication required.');
      return null;
    }
    return token;
  };

// ---------------------------------------------------------------------------
//  Token Refresh Infrastructure
//  Handles concurrent 401 responses by queuing retries while a single
//  refresh request is in flight. Prevents thundering-herd refresh calls.
// ---------------------------------------------------------------------------
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (token) {
      resolve(token);
    } else {
      reject(error);
    }
  });
  failedQueue = [];
};

/**
 * Attempt to refresh the access token using the stored refresh token.
 * This is intentionally decoupled from the auth.tsx module to avoid
 * circular dependencies (auth.tsx uses its own axios instance).
 */
const attemptTokenRefresh = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem('refresh_token');
  if (!refreshToken) return null;

  try {
    // Use a plain axios call (no interceptors) to avoid infinite loops
    const response = await axios.post(
      `${getAuthBaseUrl()}/refresh`,
      {},
      { headers: { Authorization: `Bearer ${refreshToken}` } }
    );

    if (response.status === 200 && response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
      return response.data.access_token;
    }
    return null;
  } catch {
    return null;
  }
};

/**
 * Resolve the auth base URL at runtime.
 * Uses the same NAMESPACES config as auth.tsx.
 */
const getAuthBaseUrl = (): string => {
  return NAMESPACES.users_api.auth;
};

/**
 * Force-clear all auth state and redirect to login.
 * Called when both access AND refresh tokens are invalid.
 */
const handleAuthFailure = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('user');
  localStorage.removeItem('client');
  localStorage.removeItem('local');
  localStorage.removeItem('localConfiguration');
  localStorage.removeItem('userPermissions');
  localStorage.setItem('loggedOut', 'true');

  // Only redirect if not already on the login page
  if (!window.location.pathname.includes('/login')) {
    window.location.href = '/login';
  }
};

/**
 * Setup authentication interceptor for an Axios instance.
 * 
 * REQUEST interceptor: attaches the current access token.
 * RESPONSE interceptor: on 401, transparently refreshes the token
 * and retries the failed request. Concurrent 401s are queued so only
 * one refresh request is made at a time.
 * 
 * @param axiosInstance The Axios instance to configure
 * @returns The same Axios instance (for chaining)
 */
export const setupAuthInterceptor = (axiosInstance: any) => {
  // ---- Request interceptor (unchanged) ----
  axiosInstance.interceptors.request.use((config: any) => {
    const token = GetToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // ---- Response interceptor: automatic token refresh on auth errors ----
  axiosInstance.interceptors.response.use(
    // Success — pass through
    (response: any) => response,

    // Error — intercept expired-token responses for transparent refresh
    async (error: any) => {
      const originalRequest = error.config;

      // Detect token-related errors across possible status codes:
      //   401 — standard expired/invalid token (with PROPAGATE_EXCEPTIONS)
      //   422 — some flask_jwt_extended versions return 422
      //   500 — flask_restx may swallow the JWT exception (before PROPAGATE_EXCEPTIONS fix)
      const status = error.response?.status;
      const errorCode = error.response?.data?.error;
      const errorMsg: string = (error.response?.data?.msg || error.response?.data?.message || '').toLowerCase();

      const isTokenExpiredOrInvalid =
        errorCode === 'token_expired' ||
        errorCode === 'invalid_token' ||
        errorMsg.includes('token has expired') ||
        errorMsg.includes('token ha expirado') ||
        errorMsg.includes('signature has expired');

      const isAuthError =
        (status === 401 && (isTokenExpiredOrInvalid || !errorCode)) ||
        (status === 422 && isTokenExpiredOrInvalid) ||
        (status === 500 && isTokenExpiredOrInvalid);

      if (isAuthError && !originalRequest._retry) {
        // If a refresh is already in progress, queue this request
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then((newToken) => {
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const newToken = await attemptTokenRefresh();

          if (newToken) {
            // Refresh succeeded — retry original + all queued requests
            processQueue(null, newToken);
            originalRequest.headers.Authorization = `Bearer ${newToken}`;
            return axiosInstance(originalRequest);
          } else {
            // Refresh failed — force logout and swallow the error so
            // components don't crash while the page navigates to /login.
            processQueue(error, null);
            handleAuthFailure();
            return new Promise(() => {}); // never settles — page is redirecting
          }
        } catch (refreshError) {
          processQueue(refreshError, null);
          handleAuthFailure();
          return new Promise(() => {}); // never settles — page is redirecting
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );

  return axiosInstance;
};



export const handleAxiosError = (error: any) => {
    if (axios.isAxiosError(error)) {
      // If the error comes from Axios, you might have more detailed information
      const message = error.response?.data.message || error.message;
      console.error('Axios error:', message);
    } else {
      // Handle non-Axios errors
      console.error('Unexpected error:', error);
    }
  };

/**
 * Retry helper with exponential backoff
 * @param fn Function to retry
 * @param maxRetries Maximum number of retry attempts (default: 3)
 * @param baseDelay Base delay in ms (default: 300ms)
 * @returns Promise with the result of the function
 */
export async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  maxRetries: number = 3,
  baseDelay: number = 300
): Promise<T> {
  let lastError: any;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      // Don't retry on client errors (4xx) except 429 (rate limit)
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status;
        if (status >= 400 && status < 500 && status !== 429) {
          throw error;
        }
      }
      
      // If this was the last attempt, throw the error
      if (attempt === maxRetries - 1) {
        break;
      }
      
      // Calculate exponential backoff delay: baseDelay * 2^attempt
      const delay = baseDelay * Math.pow(2, attempt);
      const jitter = Math.random() * 100; // Add jitter to avoid thundering herd
      
      console.warn(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`);
      await new Promise(resolve => setTimeout(resolve, delay + jitter));
    }
  }
  
  throw lastError;
}

export function generateGUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0,
            v = c === 'x' ? r : ((r & 0x3) | 0x8);
      return v.toString(16);
    });
  }

/**
 * Request deduplication cache to prevent multiple simultaneous identical requests.
 * Stores in-flight promises by key, so concurrent calls wait for the same promise.
 */
const inFlightRequests = new Map<string, Promise<any>>();

/**
 * Wraps an async function to deduplicate concurrent calls with the same key.
 * If a request with the same key is already in flight, subsequent calls will
 * wait for and receive the same result.
 * 
 * @param key Unique identifier for the request (e.g., "getUserPermissionState:123")
 * @param fn The async function to execute
 * @returns Promise with the result
 */
export async function deduplicateRequest<T>(
  key: string,
  fn: () => Promise<T>
): Promise<T> {
  // Check if this request is already in flight
  if (inFlightRequests.has(key)) {
    return inFlightRequests.get(key)!;
  }

  // Start the request and store the promise
  const promise = (async () => {
    try {
      const result = await fn();
      return result;
    } finally {
      // Clean up after completion (success or failure)
      inFlightRequests.delete(key);
    }
  })();

  inFlightRequests.set(key, promise);
  return promise;
}